import { useEffect, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Theme, SxProps, Tooltip, useMediaQuery } from '@mui/material';

type UserMessageActionsButtonProps = Readonly<{
  onEdit: () => void;
}>;

export const UserMessageActionsButton = ({ onEdit }: UserMessageActionsButtonProps) => {
  const isMobile = useMediaQuery('(max-width: 900px)');

  return (
    <Box id="message-edit-button" onClick={onEdit} sx={editIconSx(isMobile)}>
      <AppIcon iconName="Edit01" diameter="16px" color="Gray/500" strokeWidth="bold" />
    </Box>
  );
};

type AIMessageActionsButtonProps = Readonly<{
  onCopy: () => void;
  onThumbsUp: () => void;
  onThumbsDown: () => void;
  onBookmark: () => void;
}>;

export const AIMessageActionsButton = ({
  onCopy,
  onThumbsUp,
  onThumbsDown,
  onBookmark,
}: AIMessageActionsButtonProps) => {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [tooltipTarget, setTooltipTarget] = useState<'up' | 'down' | null>(null);
  const [tooltipText, setTooltipText] = useState('');
  const isMobile = useMediaQuery('(max-width: 900px)');

  useEffect(() => {
    if (tooltipTarget) {
      const timer = setTimeout(() => setTooltipTarget(null), 2000);

      return () => clearTimeout(timer);
    }
  }, [tooltipTarget]);

  const handleThumbsUp = () => {
    const isToggleOff = feedback === 'up';

    setFeedback(isToggleOff ? null : 'up');
    setTooltipText(isToggleOff ? 'Unliked' : 'Liked');
    setTooltipTarget('up');
    onThumbsUp();
  };

  const handleThumbsDown = () => {
    const isToggleOff = feedback === 'down';

    setFeedback(isToggleOff ? null : 'down');
    setTooltipText(isToggleOff ? 'Undisliked' : 'Disliked');
    setTooltipTarget('down');
    onThumbsDown();
  };

  const handleBookmark = () => {
    setIsBookmarked((prev) => !prev);
    onBookmark();
  };

  const dismissTooltip = () => setTooltipTarget(null);

  return (
    <Box sx={messageActionsContainer(isMobile)} onMouseLeave={dismissTooltip}>
      <Box id="message-copy-button" onClick={onCopy} sx={buttonStyle}>
        <AppIcon iconName="Copy01" diameter="16px" color="Gray/500" strokeWidth="bold" />
      </Box>
      <Box id="message-bookmark-button" onClick={handleBookmark} sx={buttonStyle}>
        <AppIcon
          iconName={isBookmarked ? 'BookmarkCheck' : 'Bookmark'}
          diameter="16px"
          color="Gray/500"
          strokeWidth="bold"
        />
      </Box>
      <Tooltip
        title={tooltipText}
        placement="top"
        arrow
        open={tooltipTarget === 'up'}
        disableHoverListener
      >
        <Box id="message-thumbs-up-button" onClick={handleThumbsUp} sx={buttonStyle}>
          <AppIcon
            iconName="ThumbsUp"
            diameter="16px"
            fill={feedback === 'up' ? 'Gray/300' : undefined}
            color="Gray/500"
            strokeWidth="bold"
          />
        </Box>
      </Tooltip>
      <Tooltip
        title={tooltipText}
        placement="top"
        arrow
        open={tooltipTarget === 'down'}
        disableHoverListener
      >
        <Box id="message-thumbs-down-button" onClick={handleThumbsDown} sx={buttonStyle}>
          <AppIcon
            iconName="ThumbsDown"
            diameter="16px"
            fill={feedback === 'down' ? 'Gray/300' : undefined}
            color="Gray/500"
            strokeWidth="bold"
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

const editIconSx = (isMobile: boolean): SxProps<Theme> => ({
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.5,
    backgroundColor: 'transparent',
  },
  opacity: 1,
  visibility: 'visible',
  ...(!isMobile && {
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
  }),
});

const buttonStyle: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-sm'],
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.5,
    backgroundColor: 'transparent',
  },
});

const messageActionsContainer =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    my: backrs.spacing['spacing-sm'],
    ...(!isMobile && {
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
      '.message-content:hover &': {
        opacity: 1,
        visibility: 'visible',
      },
    }),
  });
